import { requireEnv } from '@/lib/env';
import { isDesktop } from '@/lib/platform';
import * as amplitude from '@amplitude/analytics-browser';
import { Identify } from '@amplitude/analytics-browser';
import { plugin as engagementPlugin } from '@amplitude/engagement-browser';

const amplitudeApiKey = import.meta.env.VITE_AMPLITUDE_API_KEY;

requireEnv('Amplitude API key', amplitudeApiKey);

// Don't initialize immediately, wait for user ID
let initialized: Promise<void> | null = null;
let lastUserId: string | null = null;
let engagementBooted = false;

const initializeAmplitude = async (userId?: string) => {
  if (initialized) {
    return initialized;
  }

  if (import.meta.env.DEV) {
    return Promise.resolve();
  }

  // Add the engagement plugin for Guides and Surveys
  amplitude.add(engagementPlugin());

  const appVersion = typeof APP_VERSION !== 'undefined' ? APP_VERSION : undefined;

  initialized = amplitude.init(amplitudeApiKey, {
    // Removes the warning : https://www.docs.developers.amplitude.com/data/sdks/browser-2/#tracking-default-events
    defaultTracking: false,
    // Automatically capture events as recommended by Amplitude
    autocapture: true,
    serverZone: 'EU',
    userId: userId,
    appVersion,
  }).promise;

  // Set user properties for feature flag targeting
  const identify = new Identify()
    .set('Platform', isDesktop() ? 'electron' : 'web')
    .set('App Version', appVersion ?? 'unknown');
  amplitude.identify(identify);

  return initialized;
};

const updateUserId = (userId: string | null) => {
  if (import.meta.env.DEV) {
    return;
  }

  if (!initialized) {
    console.warn('Amplitude not initialized yet. User ID not updated:', userId);
    return;
  }

  if (lastUserId === userId) {
    return;
  }

  lastUserId = userId;

  return initialized.then(() => {
    amplitude.setUserId(userId ?? undefined);
  });
};

const logEvent = (event: string, properties?: Record<string, any>) => {
  if (import.meta.env.DEV) {
    return;
  }

  // Check if initialized before logging events
  if (!initialized) {
    console.warn('Amplitude not initialized yet. Event not logged:', event, properties);
    return;
  }

  const appVersion = typeof APP_VERSION !== 'undefined' ? APP_VERSION : 'unknown';

  return initialized.then(() => {
    amplitude.logEvent(event, { app_version: appVersion, ...properties });
  });
};

/**
 * Drain any events Amplitude has batched but not yet sent. Call before
 * navigating away from the page on an event that matters (e.g. Logout),
 * otherwise the reload aborts the in-flight request and the event is lost.
 * Bounded by a 2s timeout so a slow network can't stall the caller
 * indefinitely.
 */
const flush = async (): Promise<void> => {
  if (import.meta.env.DEV || !initialized) {
    return;
  }

  await initialized;

  const flushPromise = amplitude.flush().promise;
  const timeoutPromise = new Promise<void>((resolve) => {
    setTimeout(resolve, 2000);
  });

  await Promise.race([flushPromise, timeoutPromise]);
};

const bootGuides = () => {
  if (import.meta.env.DEV) {
    return;
  }

  if (!initialized) {
    console.warn('Amplitude not initialized yet. Guides not booted.');
    return;
  }

  if (engagementBooted) {
    return;
  }

  return initialized.then(() => {
    // Get the engagement plugin instance and boot it
    const engagement = (window as any).engagement;
    if (engagement) {
      engagement.boot();
      engagementBooted = true;
    } else {
      console.warn('Engagement plugin not available');
    }
  });
};

export const amplitudeService = {
  initialize: initializeAmplitude,
  updateUserId,
  logEvent,
  flush,
  bootGuides,
};

export default amplitudeService;
